(() => {
  // Converts kebab case to camel case
  const kebabToCamel = (term) => {
    return term
      .split('-')
      .map((word, index) => {
        return index ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;
      })
      .join('');
  };

  class AddFlow {
    constructor(root) {
      this.els = {
        root: root,
        cA11yCloseDialog: root.querySelector('.cA11yCloseDialog'),
        openOptionsButton: root.querySelector('.cProductAdd-openOptionsBtn'),
        optionsNavButton: root.querySelector('.cProductAdd-optionsNavButton'),
        optionsDialog: root.querySelector('.cProductAdd-optionsDialog'),
        optionsNavText: root.querySelector('.cProductAdd-optionsNavButton .atc-text'),
        atcSubmit: root.querySelector('.cProductAdd-submit'),
        atcSubmitText: root.querySelector('.cProductAdd-submit .atc-text'),
        atcPriceEls: root.querySelector('.atc__price .money') ? root.querySelectorAll('.atc__price .money') : root.querySelectorAll('.atc__price') ,
        atcCompareAtPrice: root.querySelector('.atc__compare-at-price .money') || root.querySelector('.atc__compare-at-price'),
        optionsContainer: root.querySelector('.cProductAdd-container'),
        stepOptions: root.querySelectorAll('.cProductAdd-options'),
        next: root.querySelectorAll('input[data-next]'),
        nextModifiers: root.querySelectorAll('input[data-next-modifier]'),
        stepCount: root.querySelectorAll('[data-step-count]'),
        stepBack: root.querySelector('.cProductAdd-navBack'),
        backdrop: root.querySelector('.cProductAdd-backdrop'),
        form: root.closest('form'),
        additionalIDs: root.querySelectorAll('[data-added-item]'),
        cartToggle: document.getElementById('cSideCart-toggle'),
        addPrescriptionLater: root.querySelector(
          '[data-add-prescription-later]'
        ),
        prescriptionUpload: root.querySelector('.cProductAdd-uploadBtn'),
        prescriptionFile: root.querySelector('[data-prescription-file]'),
        prescriptionFileName: root.querySelector(
          '.cProductAdd-prescriptionFileName'
        ),
        prescriptionUploadText: root.querySelector('.cProductAdd-uploadText'),
        cameraAction: root.querySelector('.cProductAdd-cameraAction'),
        pupilResults: root.querySelectorAll('.pd-results'),
        recipientEmail: root.querySelector(
          '[name="properties[_giftcard_recipient_email]"]'
        ),
      };

      this.locales = {
        atc: 'Add to cart',
      };

      this.step = 1;
      this.defaultStep = this.els.optionsContainer.dataset.step;
      this.defaultAction = 'submit';
      this.basePrice = parseInt(this.els.optionsContainer.dataset.basePrice || 0);
      this.baseCompareAtPrice = parseInt(this.els.optionsContainer.dataset.baseCompareAtPrice || 0);
      this.history = [];
      this.initialOptionsNavText = this.els.optionsNavText.innerHTML;

      // Event listeners
      this.registerInputChange();
      this.registerOpenOptionsClick();
      this.registerA11yCloseClick();
      this.registerNavigateOptionsClick()
      this.registerAddToCartClick();
      this.registerStepBackClick();

      if (this.els.prescriptionFile) {
        this.registerAddPrescriptionLaterChange();
        this.registerPrescriptionUpload();
      }
    }

    // Total price of the item + all customizations
    get totalPrice() {
      return [...this.els.next]
        .filter((el) => el.checked)
        .reduce((total, el) => {
          return el.name == 'id'
            ? el.dataset.price
            : (total += parseInt(el.dataset.price || 0));
        }, this.basePrice);
    }

    // Compare at price of the item + all customizations
    get totalCompareAtPrice() {
      return [...this.els.next].filter(el => el.checked).reduce((total, el) => {
        return el.name == 'id' ? el.dataset.price : total += parseInt(el.dataset.price || 0);
      }, this.baseCompareAtPrice);
    }

    /**
     * In some cases, we need to modify the next step.
     * If one of our selection has an alternate path,
     * we take it into account.
     *
     * @returns {Array.strings} Array of dataset keys
     */
    get flowPaths() {
      return [...this.els.nextModifiers]
        .filter((el) => el.checked)
        .reduce(
          (paths, input) => {
            return [
              ...paths,
              kebabToCamel(`next-${input.dataset.nextModifier}`),
            ];
          },
          ['next']
        );
    }

    set step(i) {
      this.els.stepCount.forEach((el) => {
        el.innerText = i;
      });

      this.els.stepBack.style.display = i > 1 ? 'inline-block' : 'none';

      this._step = i;
    }

    get step() {
      return this._step;
    }


    resetForm() {
      this.step = 1;
      this.disableATC();
      this.els.optionsNavText.innerHTML = this.initialOptionsNavText;
      this.goToStep(this.defaultStep);
      this.history = [];
      this.els.next.forEach((input) => {
        input.checked = false;
      });
      this.setPrice();
      
      if (this.els.prescriptionUpload) {
        this.els.prescriptionUpload.classList.remove('disabled');
      }
    }

    // Submit the add to cart request via AJAX
    async addToCart() {
      // Item is sold out, trigger Back In Stock
      if (this.els.root.classList.contains('sold-out')) {
        const { BISPopover } = window;
        if (BISPopover) {
          BISPopover.show();
        } else {
          return false;
        }
      }


      let atcSubmitText = this.els.atcSubmitText.innerHTML;
      this.els.atcSubmit.disabled = true;
      this.els.atcSubmitText.textContent = 'Adding to cart';

      const formData = new FormData(this.els.form);
      const additionalItems = [...this.els.additionalIDs].filter(
        (input) => input.checked
      );

      // Update request with all products to be added
      if (additionalItems.length) {
        const lineKey = (new Date().getTime() * 1).toString(36);

        // Add the line key as a property
        formData.append('properties[_lineKey]', lineKey);

        additionalItems.forEach(({ value: id }, index) => {
          formData.append(`items[${index}][id]`, id);
          formData.append(`items[${index}][quantity]`, 1);
          formData.append(
            `items[${index}][properties][_lineKey]`,
            `${lineKey}`
          );
        });
      }

      if (this.pdResults) {
        const pupilDistanceKey = 'properties[_Pupil Distance]';

        // Clean up the pupil distance image
        formData.delete(pupilDistanceKey);
        formData.append(pupilDistanceKey, this.pdResults);
      }

      let response = await fetch('/cart/add', {
        method: 'POST',
        body: formData,
      });

      this.resetForm();
      this.els.atcSubmit.disabled = false;
      this.els.atcSubmitText.textContent = atcSubmitText;

      if (response.ok) {
        // Analytics
        if (_tfa) {
          _tfa.push({ notify: 'event', name: 'add_to_cart', id: 1200813 });
        }

        this.open = false;
        this.els.optionsDialog.close()

        // Update and open the cart flyout + focus on sidecart
        MiniCart.update();
        this.els.cartToggle.checked = true;
        document.getElementById("cSideCartBack").focus();
      }
    }
    updatePriceText(price) {
      this.els.atcPriceEls.forEach(element => {
        element.textContent = price;
      });
    }
    // Set add to cart price
    setPrice() {
      let optionsNavText = this.els.optionsNavText.innerHTML;
      if (this.els.atcPriceEls && Currency) {
        const convertedPrice =
          this.totalPrice / 100 / Currency.rates[Currency.currentCurrency];
        const formattedPrice = new Intl.NumberFormat('en-US', {
          currency: Currency.currentCurrency,
          style: 'currency',
        })
          .format(convertedPrice)
          .replace('.00', '');
      
          this.updatePriceText(formattedPrice)
        this.els.optionsNavButton.setAttribute('aria-label', optionsNavText + " " + formattedPrice);

      }

      if (this.els.atcCompareAtPrice && Currency) {
        const convertedCAP = (this.totalCompareAtPrice / 100) / Currency.rates[Currency.currentCurrency];
        const formattedCAP = new Intl.NumberFormat('en-US', {
          currency: Currency.currentCurrency,
          style: 'currency',
        }).format(convertedCAP).replace('.00', '');
        this.els.atcCompareAtPrice.innerText = formattedCAP;
      }
    }

    enableATC() {
      this.els.root.classList.add('atc-submit-ready');
      this.els.atcSubmit.disabled = false;
      this.els.optionsNavButton.disabled = true;
    }
    disableATC() {
      this.els.root.classList.remove('atc-submit-ready');
      this.els.atcSubmit.disabled = true;
      this.els.optionsNavButton.disabled = false;
    }


    // Select option in the prescription customizer
    registerInputChange() {
      this.els.next.forEach((input) => {
        let optionsNavText;

        input.addEventListener('change', () => {
          const next = this.flowPaths.reduce((next, datasetKey) => {
            return input.dataset[datasetKey] || next;
          }, this.defaultAction);

          const nextStepTitleEl = this.els.root.querySelector(
            `.step-title[data-step='${next}']`
          );
          
          if (nextStepTitleEl) {
            optionsNavText = `Continue to ${nextStepTitleEl.innerText}`;
          }

          this.setPrice();

          // Set add to cart action if all required options are selected
          const allSelected = [...this.els.stepOptions]
            .filter((el) => el.dataset.step == this.stepName)
            .reduce((selected, el) => {
              const requiredOptions = el.querySelectorAll('[required]');
              return (
                selected &&
                [...requiredOptions].reduce((optionsSelected, option) => {
                  return optionsSelected && option.value;
                }, true)
              );
            }, true);
          
          // using aria-disabled here instead of disabled allows the user to 
          this.els.optionsNavButton.ariaDisabled = `${!allSelected}`;
          this.els.optionsNavButton.dataset.action = next;

          if (allSelected && next == this.defaultAction) {
            this.enableATC();
          } else if(allSelected && this.els.optionsNavText) {
            this.els.optionsNavText.textContent = optionsNavText;
            this.disableATC();
          }
        });
      });
    }

    goToStep(action = this.defaultStep, pushHistory = true) {
      if (!action) {
        return false;
      }

      // Store the state in a history array for back navigation
      if (pushHistory) {
        this.history.push({
          name: this.stepName || this.defaultStep,
          action: action,
          optionsNavText: this.els.optionsNavText.textContent,
          price: this.els.atcPriceEls[0].innerText,
        }); 
      }

      this.els.optionsContainer.dataset.step = action;
      this.stepName = action;

      // Set focus on the first input
      this.els.root.querySelectorAll(`.cProductAdd-options[data-step="${action}"]`).forEach(el => {
        const firstInput = el.querySelector('input');
        if (firstInput) {
          firstInput.focus();
        }
      });

      // disable the options nav button if options aren't selected for the current step
      this.els.optionsNavButton.ariaDisabled = true;
      this.els.optionsNavText.textContent = 'Make a selection';
      

      this.setPrice();

      // handle custom logic for specific steps
      if (this.stepName == 'prescription') {
        if (this.els.prescriptionFile.value) {
          this.els.prescriptionFile.dispatchEvent(new Event('change'));
        }
      } else if (this.stepName == 'pdc') {
        this.initPDCalculator();
      } else if (this.stepName == 'recipient') {
        this.els.optionsNavText.innerHTML = 'Fill out form';
        if (this.els.recipientEmail) {
          this.els.recipientEmail.dispatchEvent(new Event('change'));
        }
      }
    }

    registerA11yCloseClick() {
      this.els.cA11yCloseDialog.addEventListener('click', (event) => this.els.optionsDialog.close());
    }

    registerBackdropClick() {
      document.addEventListener('click', (event) => {
        const target = event.target;
        
        if (target === document.documentElement) {
          this.els.optionsDialog.close();
          
          document.removeEventListener('click', arguments.callee);
        }
      });
      
    }

    registerOpenOptionsClick() {
      this.els.openOptionsButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.els.optionsDialog.showModal();
        this.goToStep(this.stepName, false);
        this.registerBackdropClick();

      })
    }
    
    registerNavigateOptionsClick() {
      this.els.optionsNavButton.addEventListener('click', (e) => {
        e.preventDefault();
        if(e.target.ariaDisabled === 'true')  return;

        if (this.els.next.length) {
          const { action = this.defaultAction } = this.els.optionsNavButton.dataset;

          // If the button click didn't move forward, return
          if (action === this.stepName) {
            return;
          }

          // Move on in the flow
          if (action !== this.defaultAction) {
            this.step += 1;
            this.goToStep(action);
          } 
        } 
      });

    }
    // Add to cart click
    registerAddToCartClick() {
      this.els.atcSubmit.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Navigate back
    registerStepBackClick() {
      this.els.stepBack.addEventListener('click', (e) => {
        e.preventDefault();

        // Clear current step's inputs
        [...this.els.next]
          .filter((el) =>
            el.closest(`.cProductAdd-options[data-step='${this.stepName}']`)
          )
          .forEach((input) => {
            input.checked = false;
          });

        this.step -= 1;

        // Reset the state of the step
        const previousStep = this.history.pop();

        // disable, just in case it was enabled
        this.disableATC();

        if (previousStep) {
          this.stepName = previousStep.name;
          this.els.optionsContainer.dataset.step = previousStep.name;
          this.els.optionsNavButton.dataset.action = previousStep.action;
          this.els.optionsNavButton.disabled = false;
          this.els.optionsNavText.textContent = previousStep.optionsNavText;
          this.updatePriceText(previousStep.price)
        }
      });
    }


    // Toggle visibility of the prescription upload
    registerAddPrescriptionLaterChange() {
      this.els.addPrescriptionLater.addEventListener('change', () => {
        this.els.prescriptionUpload.classList.toggle(
          'disabled',
          this.els.addPrescriptionLater.checked
        );
      });
    }

    // Uploading a prescription file
    registerPrescriptionUpload() {
      this.els.prescriptionFile.addEventListener('change', () => {
        if (this.els.prescriptionFile.value) {
          this.els.prescriptionFileName.dataset.filename =
            this.els.prescriptionFile.value.split('\\')[2];
          this.els.prescriptionUploadText = 'Change Selection';
        } else {
          this.els.prescriptionFileName.removeAttribute('data-filename');
          this.els.prescriptionUploadText = 'Upload';
        }
      });
    }

    setPupilDistance() {
      if (this.pdResults) {
        this.els.cameraAction.innerHTML = 'Update pupil distance';
        this.els.pupilResults.forEach(el => {
          el.innerHTML = `<p>Your pupil distance is ${this.pdResults} mm.</p>`;
        });
        this.enableATC();
      }
    }

    initPDCalculator() {
      if (window.glasseson && !this.lunaInitialized) {
        const clientID = '81d39b80-0a55-41c8-a1a1-51b9a9ebab45';
        const serverURL = 'https://api.glasseson.com/prod/';
        const options = {
          profileName: 'web_en',
          flow: 'pd',
          fullPage: true,
          width: 835,
          themeColor: '#000000',
          fontFamily: 'Arial',
        };

        window.glasseson.init(clientID, serverURL, options)
          .then(() => {
            this.lunaInitialized = true;

            if (this.els.cameraAction) {
              const pd = window.localStorage.getItem('pd');
              if (pd) {
                this.pdResults = pd;
                this.setPupilDistance();
              }

              window.glasseson.setCloseCallback((result) => {
                if (result?.data.pd) {
                  this.pdResults = result.data.pd;
                  this.setPupilDistance();
                  window.localStorage.setItem('pd', this.pdResults);
                }
              });

              this.els.cameraAction.addEventListener('click', () => {
                window.glasseson.open('pd', 'pre');
              });
            }
          })
          .catch(error => {
            console.log('Luna error', error);
          });
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-product-add]').forEach((el) => {
      new AddFlow(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
